import {teeValidator} from '@teemill/common/plugins';

export default {
  inject: [
    'registerField',
    'setFieldState',
    'setFieldProperty',
    'getFieldProperty',
    'initialiseFieldObject',
    'removeFieldObject',
  ],

  props: {
    autoValidate: {
      type: Boolean,
      default: true,
    },
    value: [String, Number, Boolean, Date, Object],
    type: [String, Number, Boolean, Date, Object],
  },

  data() {
    return {
      isEditing: false,
      isEditingDelay: 1500,
      isEditingTimeout: null,
      displayError: null,
      errors: [],
      validationInProgress: false,

      unsubscribeStoreAction: null,
    };
  },

  computed: {
    inputValue: {
      get() {
        return this.getFieldProperty(this.fieldName, 'value');
      },
      set(value) {
        this.errors = [];
        this.displayError = null;

        if (this.isEditingDelay) {
          this.isEditing = true;

          if (this.autoValidate) {
            this.validate(value);
          }

          clearTimeout(this.isEditingTimeout);
          this.isEditingTimeout = setTimeout(() => {
            this.isEditing = false;
          }, this.isEditingDelay);
        }

        if (this.type === 'number') {
          if (value === '') {
            if (!this.allowEmpty) {
              value = 0;
            }
          } else {
            value = parseFloat(value);
          }
        }

        this.setFieldProperty(this.fieldName, 'value', value);

        if (this.disabled) {
          this.$emit('disabled');
        } else {
          this.$emit('input', value);
        }
      },
    },
    isValid: {
      get() {
        return this.getFieldProperty(this.fieldName, 'isValid');
      },
      set(value) {
        this.setFieldProperty(this.fieldName, 'isValid', value);
      },
    },
    elementId() {
      return `${this.$parent.formName}_${this.fieldName}`;
    },
    parentForm() {
      const checkParent = node => {
        if (!node.$parent) {
          return null;
        }

        if (node.$parent.formName) {
          return node.$parent;
        }

        return checkParent(node.$parent);
      };
      return checkParent(this);
    },
  },

  methods: {
    onFieldFocus() {
      this.isEditing = true;
      clearTimeout(this.isEditingTimeout);
      this.isEditingTimeout = setTimeout(() => {
        this.isEditing = false;
      }, this.isEditingDelay);
    },
    onFieldBlur() {
      // eslint-disable-next-line
      const vm = this;
      vm.isEditing = false;
      this.checkIfFieldValueIsValid();
    },
    validate(value, callback) {
      if (!value) {
        value = this.inputValue;
      }
      this.validationInProgress = true;
      teeValidator.validate(this.fieldName, value, !this.isEditing).then(
        result => {
          this.validationInProgress = false;
          this.isValid = result;

          this.errors = [];
          this.displayError = null;

          if (callback) {
            callback(true, []);
          }
        },
        ({errors, advisory = false}) => {
          this.validationInProgress = false;
          this.isValid = advisory;
          this.errors = errors;

          if (!this.isEditing) {
            this.displayError = this.errors[0];
          }

          if (callback) {
            if (!advisory) {
              callback(false, errors);

              this.$emit('errors');
            } else {
              callback(true, []);
            }
          }
        }
      );
    },
    checkIfFieldValueIsValid() {
      return this.isValid;
    },
  },

  created() {
    teeValidator.attach(this.fieldName, this.validationRules);
    this.initialiseFieldObject(this);

    const form = this.parentForm;
    if (form) {
      this.unsubscribeStoreAction = this.$store.subscribeAction(({type}) => {
        if (type === `forms/${form.formName}/validateFormField`) {
          if (this.autoValidate) {
            this.validate(this.inputValue, () => {
              this.displayError = this.errors[0];
            });
          }
        }
      });
    }

    if (this.inputValue && !this.value) {
      this.$emit('input', this.inputValue);
    }
  },

  beforeUnmount() {
    teeValidator.detach(this.fieldName);

    const form = this.parentForm;
    if (!!form && !form.persistent) {
      this.$store.commit(`forms/${form.formName}/onUnregisterField`, {
        fieldName: this.fieldName,
      });
      this.removeFieldObject(this);
    }

    if (this.unsubscribeStoreAction) {
      this.unsubscribeStoreAction();
    }
  },

  watch: {
    isEditing() {
      if (!this.isEditing) {
        if (this.autoValidate) {
          this.validate(this.inputValue);
        }

        if (this.errors && this.errors.length) {
          this.displayError = this.errors[0];
        }
      } else if (!this.errors.length) {
        this.displayError = null;
      }
    },
    value: {
      handler(value, oldValue) {
        if (oldValue === undefined && !value) {
          value =
            this.getFieldProperty(this.fieldName, 'value', value) ?? value;
        }

        if (this.inputValue !== value) {
          this.inputValue = value;
        }
      },
      immediate: true,
    },
    displayError(value) {
      this.$emit('validation-error', value);
    },
  },
};
